import { ArrowDown, ArrowUp, ArrowUpDown, ChevronDown, ChevronRight } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { Fragment } from 'react';
import { DataStateWrapper } from './DataStateWrapper';
import { Button } from './ui/button';
import { TABLE_SCROLLPORT_CLASS, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ScrollArea } from './ui/scroll-area';
import { cn } from '../utils';
import { useFillAvailableViewportHeight } from './useFillAvailableViewportHeight';

export interface ColumnDef<T> {
    id: string;
    header: string | (() => ReactNode);
    accessorKey?: keyof T;
    cell?: (row: T) => ReactNode;
    enableSorting?: boolean;
    sortingFn?: (a: T, b: T) => number;
    /** Fixed width for the header cell (`<th>`) and column; body cells follow via `<colgroup>`. */
    size?: number | string;
    minSize?: number;
    maxSize?: number;
    /** Cell/header alignment. Defaults to `center`. */
    align?: 'left' | 'center' | 'right';
    className?: string;
    /** Custom className for the header cell (TableHead) */
    headerClassName?: string;
    /** When true, skip the fixed-layout `truncate` wrapper so interactive controls are not clipped. */
    disableCellTruncate?: boolean;
    /** When true, column only renders in the expanded row panel (requires expandable table) */
    expandedOnly?: boolean;
}

export interface SortState {
    column: string;
    direction: 'asc' | 'desc';
}

export interface DataTableRowGroup {
    id: string;
    content: ReactNode;
}

function columnHeaderDimensionStyle<T>(column: ColumnDef<T>, sizeAsMaxWidth: boolean): CSSProperties | undefined {
    if (column.size === undefined && column.minSize === undefined && column.maxSize === undefined) {
        return undefined;
    }

    return {
        width: sizeAsMaxWidth ? column.size : undefined,
        minWidth: column.minSize ?? column.size,
        maxWidth: column.maxSize ?? (sizeAsMaxWidth ? column.size : undefined),
    };
}

function resolveColumnAlign<T>(column: ColumnDef<T>): 'left' | 'center' | 'right' {
    return column.align ?? 'center';
}

function columnAlignTextClass(align: 'left' | 'center' | 'right'): string | false {
    return align === 'right' ? 'text-right' : align === 'left' ? 'text-left' : 'text-center';
}

function hasFixedHeaderWidth<T>(column: ColumnDef<T>): boolean {
    return column.size !== undefined || column.minSize !== undefined || column.maxSize !== undefined;
}

interface DataTableProps<T> {
    columns: ColumnDef<T>[];
    data: T[];
    getRowId?: (row: T) => string;
    isLoading?: boolean;
    error?: Error | null;
    emptyMessage?: string;
    /** Default long subtitle when omitted; pass `''` to hide (see DataStateWrapper). */
    emptyDescription?: string;
    emptyIcon?: React.ComponentType<{ className?: string }>;
    /** Replaces default `h-96` empty-state wrapper height (e.g. `py-4` for compact). */
    emptyContainerHeight?: string;
    /** Smaller icon/title/spacing for embedded tables. */
    emptyCompact?: boolean;
    onSort?: (column: string, direction: 'asc' | 'desc') => void;
    sortState?: SortState;
    onRowClick?: (row: T) => void;
    rowClassName?: (row: T) => string;
    stickyHeader?: boolean;
    /**
     * When `'fillAvailable'`, table uses a fixed scroll viewport (sticky header, dual-axis ScrollArea,
     * horizontal scrollbar at viewport bottom). Height is measured from the wrapper’s top to the visual
     * viewport bottom (ResizeObserver + `visualViewport`), then clamped with `viewportMinHeight` /
     * `viewportMaxHeight`. Do not pass `stickyHeader` for the same table; it is implied.
     */
    viewportMode?: false | 'fillAvailable';
    /** Upper cap for viewport height (number = px). */
    viewportMaxHeight?: string | number;
    /** Optional floor for viewport height (number = px). */
    viewportMinHeight?: string | number;
    /** Pixels reserved below the scroll region (default 20). Used when `viewportMode` is `'fillAvailable'`. */
    viewportBottomInsetPx?: number;
    className?: string;
    /** Custom className for the header row */
    headerRowClassName?: string;
    /** Merged onto `<thead>` (e.g. `bg-card` when the table sits on a card). Never `bg-transparent` — pinned headers must be opaque. */
    tableHeaderClassName?: string;
    /** Enable expandable rows; columns with expandedOnly render in expanded panel */
    expandable?: boolean;
    /** Controlled expanded row id */
    expandedRowId?: string | null;
    /** Called when user toggles row expansion */
    onExpandedChange?: (rowId: string | null) => void;
    /** When false, clicking the row body does not toggle expansion (chevron still toggles). Default true. */
    expandOnRowBodyClick?: boolean;
    /** When false, row does not show expand chevron and is not expandable */
    getRowCanExpand?: (row: T) => boolean;
    /** Custom component for expanded row content; when provided, overrides expandedOnly columns */
    renderExpandedContent?: (row: T) => ReactNode;
    /**
     * Adds `border border-border` on the wrapper div/ScrollArea. Default is no outer frame (tables in cards use the card edge);
     * row/header horizontal dividers always come from `TableHead`/`TableCell` in the design-system table.
     */
    showWrapperBorder?: boolean;
    /**
     * Removes borders from the `<Table>` element itself via `border-0`. Use when you need to suppress table's
     * internal border styling while keeping wrapper borders controlled separately via `showWrapperBorder`.
     */
    hideTableBorder?: boolean;
    /**
     * Drops the bottom divider of the last body row (including its expanded panel). Use when nothing follows the
     * table inside its container — e.g. a table closing out a collapsible group or a card — so no rule is left
     * separating the rows from padding. Keep the default when pagination or a footer sits directly below.
     */
    hideLastRowBorder?: boolean;
    /** Tighter header/body padding and slightly smaller header type (dense / embedded tables). */
    compact?: boolean;
    /**
     * `fixed` uses CSS `table-layout: fixed` and `w-full` so column widths stay stable when cell
     * content changes (e.g. react-select labels). `content` lets configured widths act as minimums
     * while allowing columns to grow around non-wrapping content. Default `auto` does not force
     * `table-fixed` (avoids smashing narrow viewports); sized columns contribute to `minWidth` instead.
     */
    tableLayout?: 'auto' | 'content' | 'fixed';
    /** Keep every column title on one line (EN/DE). Horizontal scroll instead of wrapping. */
    nowrapHeaders?: boolean;
    /** Inserts a full-width heading row when this group id differs from the preceding data row. Data must already be grouped and ordered. */
    getRowGroup?: (row: T) => DataTableRowGroup | null;
}

export function DataTable<T extends Record<string, any>>({
    columns,
    data,
    getRowId = row => row.id,
    isLoading = false,
    error = null,
    emptyMessage = 'No data available',
    emptyDescription,
    emptyIcon,
    emptyContainerHeight,
    emptyCompact,
    onSort,
    sortState,
    onRowClick,
    rowClassName,
    stickyHeader = true,
    viewportMode = false,
    viewportMaxHeight,
    viewportMinHeight,
    viewportBottomInsetPx,
    className,
    headerRowClassName,
    tableHeaderClassName,
    expandable = false,
    expandedRowId = null,
    onExpandedChange,
    expandOnRowBodyClick = true,
    getRowCanExpand,
    renderExpandedContent,
    showWrapperBorder = false,
    hideTableBorder = false,
    hideLastRowBorder = false,
    compact = false,
    tableLayout = 'auto',
    nowrapHeaders = false,
    getRowGroup,
}: DataTableProps<T>) {
    const handleSort = (column: ColumnDef<T>) => {
        if (!column.enableSorting || !onSort) return;

        const newDirection = sortState?.column === column.id && sortState?.direction === 'asc' ? 'desc' : 'asc';

        onSort(column.id, newDirection);
    };

    const sortIconClass = compact ? 'h-3.5 w-3.5' : 'h-4 w-4';

    const getSortIcon = (column: ColumnDef<T>) => {
        if (!column.enableSorting) return null;

        if (sortState?.column === column.id) {
            return sortState.direction === 'asc' ? <ArrowUp className={sortIconClass} /> : <ArrowDown className={sortIconClass} />;
        }

        return <ArrowUpDown className={cn(sortIconClass, 'opacity-50')} />;
    };

    const mainColumns = expandable ? columns.filter(c => !c.expandedOnly) : columns;
    const expandedColumns = expandable ? columns.filter(c => c.expandedOnly) : [];
    const displayColumns = expandable ? [{ id: '__expand__', header: '', size: 32, minSize: 32, maxSize: 32 } as ColumnDef<T>, ...mainColumns] : mainColumns;

    const toggleExpand = (row: T) => {
        if (!expandable || !onExpandedChange) return;
        const canExpand = getRowCanExpand ? getRowCanExpand(row) : true;
        if (!canExpand) return;
        const rowId = getRowId(row);
        onExpandedChange(expandedRowId === rowId ? null : rowId);
    };

    const useFillAvailableViewport = viewportMode === 'fillAvailable';
    const effectiveStickyHeader = stickyHeader || useFillAvailableViewport;
    /** Only explicit `fixed` uses table-fixed; auto + minSize used to force fixed and smash narrow viewports. */
    const useFixedTableLayout = tableLayout === 'fixed';
    const sizeAsMaxWidth = tableLayout !== 'content';
    const truncateDimensionedColumns = useFixedTableLayout;
    const tableMinWidthPx = displayColumns.reduce((sum, column) => {
        if (typeof column.minSize === 'number') return sum + column.minSize;
        if (typeof column.size === 'number') return sum + column.size;
        return sum + (hasFixedHeaderWidth(column) ? 120 : 0);
    }, 0);

    const { wrapperRef, heightPx } = useFillAvailableViewportHeight({
        enabled: useFillAvailableViewport,
        viewportMaxHeight,
        viewportMinHeight,
        viewportBottomInsetPx,
    });

    const tableContent = (
        <>
            <TableHeader className={tableHeaderClassName}>
                <TableRow className={cn(headerRowClassName, 'hover:bg-transparent')}>
                    {displayColumns.map(column => {
                        const align = resolveColumnAlign(column);
                        return (
                            <TableHead
                                key={column.id}
                                className={cn(
                                    'font-sans h-auto py-2',
                                    nowrapHeaders ? 'whitespace-nowrap' : 'whitespace-normal',
                                    compact ? 'text-xs' : 'min-h-[52px]',
                                    column.headerClassName,
                                    columnAlignTextClass(align),
                                )}
                                style={columnHeaderDimensionStyle(column, sizeAsMaxWidth)}
                            >
                                <div
                                    className={cn(
                                        'flex min-w-0 items-center gap-2',
                                        compact && 'gap-1.5',
                                        align === 'center' && 'w-full justify-center',
                                        align === 'right' && 'w-full justify-end',
                                    )}
                                >
                                    <div className={cn('min-w-0 leading-tight', nowrapHeaders ? 'whitespace-nowrap' : 'whitespace-normal break-words')}>
                                        {typeof column.header === 'function' ? column.header() : <span title={column.header}>{column.header}</span>}
                                    </div>
                                    {column.enableSorting && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className={cn('shrink-0 p-0', compact ? 'h-6 w-6' : 'h-8 w-8')}
                                            onClick={() => handleSort(column)}
                                            aria-label={`Sort by ${typeof column.header === 'string' ? column.header : column.id}`}
                                        >
                                            {getSortIcon(column)}
                                        </Button>
                                    )}
                                </div>
                            </TableHead>
                        );
                    })}
                </TableRow>
            </TableHeader>
            <TableBody className={cn(hideLastRowBorder && '[&>tr:last-child>td]:border-b-0')}>
                {data.map((row, rowIndex) => {
                    const rowId = getRowId(row);
                    const rowGroup = getRowGroup?.(row) ?? null;
                    const previousRowGroup = rowIndex > 0 ? (getRowGroup?.(data[rowIndex - 1]) ?? null) : null;
                    const shouldRenderGroup = rowGroup !== null && rowGroup.id !== previousRowGroup?.id;
                    const deferRowRendering = data.length > 20 && rowIndex > 14;
                    const isExpanded = expandable && expandedRowId === rowId;
                    const canExpand = expandable && (getRowCanExpand ? getRowCanExpand(row) : true);
                    const rowBodyTogglesExpand = expandable && canExpand && expandOnRowBodyClick;
                    const hasRowClick = rowBodyTogglesExpand || !!onRowClick;
                    const handleRowBodyClick = () => {
                        if (onRowClick) {
                            onRowClick(row);
                            return;
                        }
                        if (rowBodyTogglesExpand) {
                            toggleExpand(row);
                        }
                    };

                    return (
                        <Fragment key={rowId}>
                            {shouldRenderGroup && rowGroup ? (
                                <TableRow className="hover:bg-transparent">
                                    <TableCell colSpan={displayColumns.length} className={cn('bg-background px-4 py-3', compact && 'px-2 py-2.5')}>
                                        {rowGroup.content}
                                    </TableCell>
                                </TableRow>
                            ) : null}
                            <TableRow
                                onClick={hasRowClick ? handleRowBodyClick : undefined}
                                className={cn(
                                    hasRowClick && 'cursor-pointer hover:bg-muted/50',
                                    isExpanded && 'bg-muted/30',
                                    deferRowRendering && 'ram-deferred-table-row',
                                    rowClassName?.(row),
                                )}
                            >
                                {displayColumns.map(column => {
                                    if (column.id === '__expand__') {
                                        return (
                                            <TableCell key={column.id} className={cn('overflow-hidden', compact && 'py-1 px-2', column.className)}>
                                                {canExpand ? (
                                                    <button
                                                        type="button"
                                                        className="inline-flex rounded-sm p-0.5 text-muted-foreground hover:bg-muted"
                                                        aria-expanded={isExpanded}
                                                        aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            toggleExpand(row);
                                                        }}
                                                    >
                                                        {isExpanded ? (
                                                            <ChevronDown className={cn(compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
                                                        ) : (
                                                            <ChevronRight className={cn(compact ? 'h-3.5 w-3.5' : 'h-4 w-4')} />
                                                        )}
                                                    </button>
                                                ) : (
                                                    <span className="block w-4" />
                                                )}
                                            </TableCell>
                                        );
                                    }
                                    const value = column.accessorKey ? row[column.accessorKey] : undefined;
                                    const content = column.cell ? column.cell(row) : value;
                                    const align = resolveColumnAlign(column);
                                    const shouldTruncate = truncateDimensionedColumns && hasFixedHeaderWidth(column) && !column.disableCellTruncate;

                                    return (
                                        <TableCell
                                            key={column.id}
                                            className={cn(
                                                'font-sans',
                                                compact && 'py-2 px-2',
                                                shouldTruncate && 'max-w-0',
                                                column.className,
                                                columnAlignTextClass(align),
                                            )}
                                        >
                                            <div
                                                className={cn(
                                                    'flex min-w-0 w-full items-center overflow-hidden',
                                                    align === 'center' && 'justify-center',
                                                    align === 'right' && 'justify-end',
                                                )}
                                            >
                                                {shouldTruncate ? <div className="min-w-0 truncate">{content}</div> : content}
                                            </div>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            {isExpanded && (renderExpandedContent || expandedColumns.length > 0) && (
                                <TableRow className="hover:bg-transparent">
                                    <TableCell colSpan={displayColumns.length} className="p-0">
                                        {renderExpandedContent ? (
                                            renderExpandedContent(row)
                                        ) : (
                                            <div className="bg-muted/30 px-6 py-4">
                                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-x-8 gap-y-0 max-w-4xl">
                                                    {expandedColumns.map(col => {
                                                        const label = typeof col.header === 'function' ? '' : col.header;
                                                        const value = col.cell ? col.cell(row) : col.accessorKey ? row[col.accessorKey] : undefined;
                                                        return (
                                                            <div key={col.id} className="flex items-start gap-2 py-1 min-w-0">
                                                                <div className="min-w-0 flex-1">
                                                                    <span className="text-muted-foreground text-xs block">{label}</span>
                                                                    <span className="text-sm truncate block">{String(value ?? '—')}</span>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )}
                        </Fragment>
                    );
                })}
            </TableBody>
        </>
    );

    const tableEl = (
        <Table
            noWrapper={effectiveStickyHeader}
            stickyHeader={effectiveStickyHeader}
            className={cn('font-sans', useFixedTableLayout ? 'w-full table-fixed' : 'min-w-max', hideTableBorder && 'border-0')}
            style={tableMinWidthPx > 0 ? { minWidth: tableMinWidthPx } : undefined}
        >
            <colgroup>
                {displayColumns.map(column => (
                    <col key={column.id} style={columnHeaderDimensionStyle(column, sizeAsMaxWidth)} />
                ))}
            </colgroup>
            {tableContent}
        </Table>
    );

    return (
        <DataStateWrapper
            isLoading={isLoading}
            isError={!!error}
            error={error}
            isEmpty={data.length === 0}
            emptyMessage={emptyMessage}
            emptyDescription={emptyDescription}
            emptyIcon={emptyIcon}
            emptyCompact={emptyCompact}
            containerHeight={emptyContainerHeight ?? 'h-96'}
        >
            {useFillAvailableViewport ? (
                <div ref={wrapperRef} className={cn('w-full rounded-md', showWrapperBorder && 'border border-border', className)} style={{ height: heightPx }}>
                    <ScrollArea orientation="both" className="h-full w-full rounded-[inherit]">
                        {tableEl}
                    </ScrollArea>
                </div>
            ) : (
                <div
                    className={cn('overflow-x-auto rounded-md', showWrapperBorder && 'border border-border', stickyHeader && TABLE_SCROLLPORT_CLASS, className)}
                >
                    {tableEl}
                </div>
            )}
        </DataStateWrapper>
    );
}
